
import Vue from 'vue';
import { Icon } from '@descomplica/vue-components';
import { fileUpload } from '../../helpers/upload';
import i18n from '../../i18n';
import { CancelTokenSource } from 'axios';
import { ErrorHandler } from '@/helpers/errorHandler';
import { FileModel } from '../../models/FileModel';
import { UserModel } from '@descomplica/dex-models';
import SentryClient from '@/helpers/sentry';

const ONE_MB = 1024 * 1024;

export interface FileInformation extends FileModel {
  status: 'pending' | 'uploading' | 'finished' | 'error';
  file: File;
  url: string;
  timestamp: number;
  sizeInMB: string;
  uploadControl?: CancelTokenSource;
}

export default Vue.extend({
  components: {
    Icon,
  },
  props: {
    maxFileSizeInMB: {
      type: Number,
      default: 20,
    },
    fileToUpload: {
      type: File,
      default: null,
    },
    user: {
      type: Object as Vue.PropType<UserModel>,
      required: true,
    },
    reset: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array as Vue.PropType<FileInformation[]>,
      default: () => [],
    },
  },
  data() {
    return {
      progressPercent: 0,
      progressControl: null as any,
      filesArr: this.files,
    };
  },
  computed: {
    errorMessage(): string {
      const count = this.filesArr.filter((fileInfo) => fileInfo.status === 'error').length;

      if (!count) return '';

      const indexMsg = count > 1 ? 2 : 1;

      return `Falha no envio de ${i18n.tc('filesCount', indexMsg, { count })}`;
    },
  },
  watch: {
    reset(current: Boolean, previous: Boolean) {
      if (current && !previous) {
        this.progressPercent = 0;
        this.progressControl = null;
        this.filesArr = [];
        this.emitFilesUploaded();
      }
    },
    fileToUpload(current: File) {
      const fileSizeInMb = this.castBitToMB(current.size);

      if (fileSizeInMb > this.maxFileSizeInMB) {
        ErrorHandler.showErrorMessage({
          error: new Error(
            `O tamanho máximo permitido para upload é de ${this.maxFileSizeInMB} MB mas o arquivo ${
              current.name
            } possui ${fileSizeInMb.toFixed(2)} MB.`,
          ),
        });
      } else {
        this.addFile(current);
      }
    },
  },
  methods: {
    castBitToMB(bits: number) {
      return bits ? bits / ONE_MB : 0;
    },
    addFile(file: File) {
      const fileInfo: FileInformation = {
        file,
        status: 'pending',
        url: '',
        name: '',
        size: 0,
        type: '',
        timestamp: new Date().getTime(),
        sizeInMB: `${this.castBitToMB(file.size).toFixed(2)} MB`,
      };

      this.updateFilesArr(fileInfo);
      this.uploadFile();
    },
    updateFilesArr(fileInfo: FileInformation) {
      const fileIndex = this.filesArr.findIndex((file) => file.timestamp === fileInfo.timestamp);

      if (fileIndex >= 0) this.filesArr[fileIndex] = fileInfo;
      else this.filesArr.push(fileInfo);
    },
    emitFilesUploaded() {
      const files: FileModel[] = this.filesArr.map((file) => ({
        name: file.file?.name || file.name,
        url: file.url,
        size: file.file?.size || file.size,
        type: file.file?.type || file.type,
      }));
      this.$emit('onFinishUploads', files);
    },
    async uploadFile(): Promise<void | null> {
      if (this.progressPercent) return null;

      const pendingFiles = this.filesArr.filter((file) => file.status === 'pending');

      if (!pendingFiles.length) {
        this.emitFilesUploaded();
        return null;
      }

      const fileInfo = pendingFiles[0];
      fileInfo.status = 'uploading';
      this.updateFilesArr(fileInfo);

      const reader = new FileReader();
      return new Promise<void>((resolve) => {
        reader.readAsDataURL(fileInfo.file);
        reader.onload = async () => {
          await this.startUpload(reader.result, fileInfo);

          resolve();
        };

        reader.onerror = () => {
          SentryClient.captureError(new Error(reader.error?.message));

          resolve();
        };
      });
    },
    deleteFile(fileInfo: FileInformation) {
      this.filesArr = this.filesArr.filter(
        (file) => file.timestamp !== fileInfo.timestamp || file.url !== fileInfo.url,
      );
      this.emitFilesUploaded();
    },
    updateProgress(params: { percent: number }) {
      this.progressPercent = params.percent;
    },
    setCancelToken(cancelToken: CancelTokenSource) {
      const uploadingFile = this.filesArr.find((file) => file.status === 'uploading');
      if (uploadingFile) {
        this.updateFilesArr({
          ...uploadingFile,
          uploadControl: cancelToken,
        });
      }
    },
    async startUpload(fileBase64: any, fileInfo: FileInformation) {
      if (!fileBase64 && !fileInfo) {
        return false;
      }
      const uploadInfo = await fileUpload(
        fileBase64,
        this.user.id,
        this.updateProgress,
        this.setCancelToken,
      );

      this.progressPercent = 0;
      if (uploadInfo.success) {
        this.updateFilesArr({
          ...fileInfo,
          status: 'finished',
          url: uploadInfo.url,
        });
      } else if (!uploadInfo.cancelled) {
        this.updateFilesArr({
          ...fileInfo,
          status: 'error',
        });
      } else {
        this.deleteFile(fileInfo);
      }

      this.uploadFile();

      return true;
    },
    cancelUpload(fileInfo: FileInformation) {
      if (fileInfo.uploadControl) {
        fileInfo.uploadControl.cancel();
      }
      this.deleteFile(fileInfo);
    },
    tryAgain(fileInfo: FileInformation) {
      fileInfo.status = 'pending';
      const fileIndex = this.filesArr.findIndex((file) => file.timestamp === fileInfo.timestamp);
      this.filesArr[fileIndex] = fileInfo;
      this.uploadFile();
    },
  },
});
